import React, { useRef } from 'react';
import { TERRAIN_SPECIFICATION } from '../../../../components/map-interaction/TerrainControl';
import { UDViewMapStyle } from '../../../../udView/udview';
import { useEvent } from '../../../contexts/event-bus';
import WorkspaceItemContext from '../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import { useSafeEffect } from '../../../hooks/use-safe-effect';
import { patchFocusedWorkspaceView, store } from '../../../state/store';
import { useWorkspace } from '../hooks/use-view';

const RESUME_ROTATION_AFTER_MOVE_MS = 1000;

export default function MapViewSync() {
    const dispatch = useAppDispatch();
    const context = React.useContext(WorkspaceItemContext);
    const map = context.map;
    const mapState = useWorkspace((x) => x.view.map);
    const viewModified = useWorkspace((x) => x.viewModified);
    const viewId = useWorkspace((x) => x.view.id);
    const mapMoved = useRef(false);
    const previousMapStyle = useRef<UDViewMapStyle>(undefined);
    const isDark = useAppSelector((x) => x.settings.isDark);

    useSafeEffect(() => {
        const view = store.getState().workspace.items[context.id].view;

        // If not default view
        if (view.id) {
            return;
        }

        // If not one of those 2 styles, then don't override what the user has set
        if (view.map.style !== UDViewMapStyle.light && view.map.style !== UDViewMapStyle.dark) {
            return;
        }

        const targetStyle = isDark ? UDViewMapStyle.dark : UDViewMapStyle.light;
        // If already using target style
        if (view.map.style === targetStyle) {
            return;
        }

        dispatch(patchFocusedWorkspaceView((v) => (v.map.style = targetStyle), context.id));
    }, [isDark]);

    useSafeEffect(() => {
        // First load
        if (previousMapStyle.current === undefined) {
            previousMapStyle.current = mapState.style;
            return;
        }

        // If the style hasn't changed
        if (previousMapStyle.current === mapState.style) {
            return;
        }

        previousMapStyle.current = mapState.style;
        map?.loadUdViewStyle(mapState.style);
    }, [map, mapState.style]);

    const writeMapStateToUdView = React.useCallback(() => {
        if (!map) {
            return;
        }

        const center = map.getCenter();
        dispatch(
            patchFocusedWorkspaceView((x) => {
                x.map.lat = center.lat;
                x.map.lon = center.lng;
                x.map.zoom = map.getZoom();
                x.map.pitch = map.getPitch();
                x.map.bearing = map.getBearing();
                x.map.terrain = !!map.getTerrain();
            }, context.id)
        );
    }, [context.id, map]);

    // Sync map projection to the map
    useSafeEffect(() => {
        map?.setProjection({
            type: mapState.useGlobeView ? 'globe' : 'mercator'
        });

        if (mapState.useGlobeView) {
            map?.setPitch(0);
            map?.setBearing(0);
            map?.dragRotate?.disable();
            map?.touchPitch?.disable();
            map?.touchZoomRotate?.disable();
            map?.keyboard?.disableRotation();
        } else {
            map?.dragRotate?.enable();
            map?.touchPitch?.enable();
            map?.touchZoomRotate?.enable();
            map?.keyboard?.enableRotation();
        }
    }, [map, mapState.useGlobeView]);

    useSafeEffect(() => {
        map?.setTerrain(mapState.terrain ? TERRAIN_SPECIFICATION : null);
    }, [map, mapState.terrain]);

    useSafeEffect(() => {
        if (!map || !mapState.useGlobeView || !mapState.globeAutoRotate) { return; }

        let lastTime: number | null = null;
        let isPaused = false;
        let pauseTimer: number | null = null;
        let animationFrameId: number;

        const pauseRotation = () => {
            isPaused = true;
            if (pauseTimer) {
                self.clearTimeout(pauseTimer);
            }
            pauseTimer = self.setTimeout(() => { isPaused = false; }, RESUME_ROTATION_AFTER_MOVE_MS);
        };

        const unsubMousedown = map.on('mousedown', pauseRotation).unsubscribe;
        const unsubTouchstart = map.on('touchstart', pauseRotation).unsubscribe;
        const unsubZoomStart = map.on('zoom', pauseRotation).unsubscribe;

        const rotate = (timestamp: number) => {
            if (!isPaused) {
                if (lastTime !== null) {
                    const delta = (timestamp - lastTime) / 1000;
                    const center = map.getCenter();
                    map.setCenter({ lng: center.lng - mapState.globeAutoRotateSpeed * delta, lat: center.lat }, { globeAutoRotate: true });
                }
                lastTime = timestamp;
            } else {
                lastTime = null;
            }
            animationFrameId = requestAnimationFrame(rotate);
        };

        animationFrameId = requestAnimationFrame(rotate);

        return () => {
            cancelAnimationFrame(animationFrameId);
            unsubMousedown();
            unsubTouchstart();
            unsubZoomStart();
            if (pauseTimer) {
                self.clearTimeout(pauseTimer);
            }
        };
    }, [map, mapState.useGlobeView, mapState.globeAutoRotate, mapState.globeAutoRotateSpeed]);

    // On leave map write changes to ud view
    useSafeEffect(() => {
        return () => {
            // If map wasn't moved don't override map state in ud view because it'll set view state to modified
            if (mapMoved.current) {
                writeMapStateToUdView();
            }
        };
    }, [writeMapStateToUdView]);

    useSafeEffect(() => {
        mapMoved.current = false;
        return map?.on('move', (e) => {
            // Sometimes move is triggered by map resize events, ignore those. Also ignore move events triggered by globe auto rotate
            if (('initialUdViewLoad' in e && e.initialUdViewLoad) || ('globeAutoRotate' in e && e.globeAutoRotate) || ('0' in e && e['0'] instanceof ResizeObserverEntry)) {
                return;
            }
            // If it's the first map move write the changes to udview to mark it as edited but ignore the rest
            if (!mapMoved.current) {
                writeMapStateToUdView();
            }
            mapMoved.current = true;
        }).unsubscribe;
    }, [map, writeMapStateToUdView, viewModified]);

    useEvent(
        'get-map-state',
        React.useCallback(
            (e) => {
                // Check if it's this workspace and this view
                if (context.id !== e.workspaceId || !map || e.viewId !== viewId) {
                    return;
                }
                const center = map.getCenter();
                e.result({
                    lat: center.lat,
                    lon: center.lng,
                    zoom: map.getZoom(),
                    pitch: map.getPitch(),
                    bearing: map.getBearing(),
                    terrain: !!map.getTerrain()
                });
            },
            [map, viewId, context]
        )
    );

    return <></>;
}
